import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Stack,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  TextField,
  InputAdornment,
  Divider,
  Alert,
  FormControlLabel,
} from "@mui/material";
import {
  Close,
  Search,
  Info,
  CheckCircle,
  Refresh,
  MoveToInbox,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import DialogLoading from "../../../App/DialogLoading";

const AssetImportManager = ({
  open,
  onClose,
  allAssets = [],
  availableAssets = [],
  importedAssets = [],
  onImportSelected,
  onRefreshAssets,
}) => {
  const theme = useTheme();
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const getAssetStatus = (isImported) => {
    return isImported
      ? {
          color: "default",
          icon: <CheckCircle fontSize='small' />,
          text: "Imported",
        }
      : {
          color: "success",
          text: "Available",
        };
  };

  const getSearchText = (asset) =>
    [
      asset.assetName,
      asset.category,
      asset.description,
      asset.source,
      asset.sourceSchedule,
    ]
      .map((value) => String(value || "").toLowerCase())
      .join(" ");

  // Filter assets based on search term
  const filteredAssets = allAssets.filter((asset) =>
    getSearchText(asset).includes(searchTerm.toLowerCase())
  );

  // Handle individual asset selection
  const handleAssetSelection = (assetId) => {
    setSelectedAssets((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    );
  };

  useEffect(() => {
    setSelectAll(
      selectedAssets.length > 0 &&
        selectedAssets.length === filteredAssets.length
    );
  }, [selectedAssets, filteredAssets]);

  const handleSelectAll = () => {
    if (selectAll || selectedAssets.length !== 0) {
      setSelectedAssets([]);
      setSelectAll(false);
    } else {
      const allIds = availableAssets.map((asset) => asset.assetId);
      setSelectedAssets(allIds);
      if (availableAssets.length === allAssets.length) setSelectAll(true);
    }
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    // Reset selections when search changes
    setSelectedAssets([]);
    setSelectAll(false);
  };

  // Handle import selected assets
  const handleImportSelected = () => {
    setIsLoading(true);
    const assetsToImport = availableAssets.filter((asset) =>
      selectedAssets.includes(asset.assetId)
    );
    onImportSelected(assetsToImport);
    setSelectedAssets([]);
    setSelectAll(false);
    setIsLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='lg'
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
          minHeight: "70vh",
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <MoveToInbox color='primary' />
          </Box>
          <Box flex={1}>
            <Typography variant='h6' fontWeight={600} color='text.primary'>
              Import Hire Purchase Assets
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Select assets from FA Additions or FA Register to import
            </Typography>
          </Box>
          {onRefreshAssets && (
            <Tooltip title='Refresh asset list'>
              <IconButton
                onClick={onRefreshAssets}
                disabled={isLoading}
                sx={{
                  color: "text.secondary",
                  "&:hover": {
                    color: "primary.main",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            onClick={onClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Close />
          </IconButton>
        </Paper>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Loading indicator */}
          {isLoading && (
            <DialogLoading
              title='Importing your assets...'
              content='Just a moment — preparing the data.'
            />
          )}

          {/* Search and Controls */}
          <Stack direction='row' spacing={2} sx={{ mb: 3 }}>
            <TextField
              placeholder='Search assets...'
              value={searchTerm}
              onChange={handleSearchChange}
              size='small'
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search color='action' />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                    boxShadow: (theme) =>
                      `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  indeterminate={selectedAssets.length > 0 && !selectAll}
                />
              }
              label='Select All'
            />
          </Stack>

          {/* Summary Info */}
          <Alert
            color='primary'
            severity='info'
            icon={<Info />}
            sx={{
              mb: 3,
              borderRadius: 2,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Stack
              direction='row'
              justifyContent='space-between'
              alignItems='center'
            >
              <Typography variant='body2'>
                {filteredAssets.length} assets available -{" "}
                {selectedAssets.length} selected
              </Typography>
              <Typography variant='body2' fontWeight={600}>
                Total Value:{" "}
                {formatAmount(
                  filteredAssets
                    .filter((asset) => selectedAssets.includes(asset.assetId))
                    .reduce((sum, asset) => sum + (asset.cost || 0), 0)
                )}
              </Typography>
            </Stack>
          </Alert>

          {/* Assets Table */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              maxHeight: "400px",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                      indeterminate={selectedAssets.length > 0 && !selectAll}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant='subtitle2' fontWeight={600}>
                      Asset Name
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography variant='subtitle2' fontWeight={600}>
                      Category
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography variant='subtitle2' fontWeight={600}>
                      Source
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <Typography variant='subtitle2' fontWeight={600}>
                      Cost
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Typography variant='subtitle2' fontWeight={600}>
                      Status
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAssets.map((asset) => {
                  const isImported = importedAssets.some(
                    (item) => item.assetId === asset.assetId
                  );
                  const status = getAssetStatus(isImported);
                  const isSelected = selectedAssets.includes(asset.assetId);

                  return (
                    <TableRow
                      key={asset.assetId}
                      hover
                      selected={isSelected}
                      sx={{
                        cursor: "pointer",
                        "&:hover": {
                          bgcolor: (theme) =>
                            alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleAssetSelection(asset.assetId)}
                          disabled={isImported}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant='body2' fontWeight={500}>
                            {asset.assetName}
                          </Typography>
                          {asset.description && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              {asset.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          label={asset.category || "Uncategorized"}
                          size='small'
                          variant='outlined'
                          sx={{
                            borderRadius: 1,
                            textTransform: "capitalize",
                          }}
                        />
                      </TableCell>
                      <TableCell align='center'>
                        <Stack spacing={0.5} alignItems='center'>
                          <Chip
                            label={asset.source || "FA Additions"}
                            size='small'
                            color={
                              asset.source === "FA Register"
                                ? "primary"
                                : "default"
                            }
                            variant='outlined'
                            sx={{ borderRadius: 1, fontWeight: 600 }}
                          />
                          {asset.sourceSchedule && (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              {asset.sourceSchedule}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align='right'>
                        <Typography variant='body2' fontWeight={500}>
                          {formatAmount(asset.cost)}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Chip
                          icon={status.icon}
                          label={status.text}
                          size='small'
                          color={status.color}
                          variant='outlined'
                          sx={{
                            borderRadius: "9999px",
                            fontWeight: 500,
                            px: 1.5,
                            py: 0.25,
                            transition: "all 0.25s ease-in-out",
                            borderColor: (theme) =>
                              theme.palette[status.color]?.main ||
                              theme.palette.grey[400],
                            backgroundColor: (theme) =>
                              theme.palette.action.hover,
                            "& .MuiChip-icon": {
                              fontSize: "1.1rem",
                              marginLeft: "4px",
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* No results message */}
          {filteredAssets.length === 0 && !isLoading && (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                color: "text.secondary",
              }}
            >
              <Typography variant='body1' gutterBottom>
                {searchTerm
                  ? "No assets found matching your search"
                  : "No assets available to import"}
              </Typography>
              <Typography variant='body2'>
                {searchTerm
                  ? "Try adjusting your search terms"
                  : "Check back later or refresh to see new assets"}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2, justifyContent: "space-between" }}>
        <Box>
          <Typography variant='body2' color='text.secondary'>
            {selectedAssets.length} of {filteredAssets.length} assets selected
          </Typography>
        </Box>
        <Stack direction='row' spacing={2}>
          <Button
            onClick={onClose}
            variant='outlined'
            color='primary'
            sx={{
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImportSelected}
            variant='contained'
            color='primary'
            disabled={selectedAssets.length === 0 || isLoading}
            startIcon={<MoveToInbox />}
          >
            Import Selected ({selectedAssets.length})
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default AssetImportManager;
